import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Column, Lookup } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, SimpleItem, Label, FormRef } from "devextreme-react/form";
import { FieldDataChangedEvent } from "devextreme/ui/form";
import IChannelManager from "../../models/IChannelManager";
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import { useContextPopupPage } from '../../components/popups/PopupPage';
import useLookUpBase from '../../components/selectbox/LookupBase';
import Guid from '../../models/Guid';
import './gestion-channel-manager.css';

interface IGestionChannelManagerProps {
  isModeSelection?: boolean,
}

const FALLBACK_DATE = new Date(0);

const matchesGuid = (left: Guid | string | undefined, right: Guid | string | undefined) => {
  if (left == null || right == null) return false;
  if (typeof (left as Guid).equals === 'function') {
    return (left as Guid).equals(right as Guid);
  }
  return String(left).toLowerCase() === String(right).toLowerCase();
};

export default function GestionChannelManager({ isModeSelection }: IGestionChannelManagerProps) {
  const channelManagerFormDataSource = useCustomDataSource<IChannelManager, IChannelManager>({ path: "channelManager", primaryKey: "idChannelManager" });
  const { setMainForm, selectedDataRow: selectedChannelManager, setSelectedDataRow, mode, onFieldDataChanged, setMode, isFieldModifier, } = channelManagerFormDataSource;
  const formRef = useRef<FormRef>(null);
  const { setSelectedValue } = useContextPopupPage();

  const { editorOptions: uniteEditorOptions, data: uniteLookupData } = useLookUpBase({ controllerName: "unite", displayExpr: "code", showFind: false, showClear: mode !== Mode.Lecture, isModeModifier: mode !== Mode.Lecture, width: '20rem', });

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  const selectedUniteCode = useMemo(() => {
    if (!selectedChannelManager?.idUnite || matchesGuid(selectedChannelManager.idUnite, Guid.empty)) {
      return '';
    }
    const match = (uniteLookupData || []).find((unite) => matchesGuid(unite.id, selectedChannelManager.idUnite));
    return match?.code ?? '';
  }, [selectedChannelManager?.idUnite, uniteLookupData]);

  const prixEditorOptions = useMemo(() => ({
    format: '#,##0.00 $',
    width: '12rem',
  }), []);

  const systemeFormData = useMemo(() => ({
    dateCreation: FALLBACK_DATE,
    dateModification: FALLBACK_DATE,
    dateSuppression: null,
    idUsagerCreation: Guid.empty,
    idUsagerModification: Guid.empty,
    idUsagerSuppression: null,
  }), []);

  const handleFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    onFieldDataChanged(e);
    if (e.dataField !== 'idUnite') {
      return;
    }
    const selectedUnite = (uniteLookupData || []).find((unite) => matchesGuid(unite.id, e.value));
    setSelectedDataRow((prev) => (prev ? {
      ...prev,
      idUnite: e.value ?? Guid.empty,
      idTypeUnite: selectedUnite?.idParent ?? Guid.empty,
    } : prev));
  }, [onFieldDataChanged, setSelectedDataRow, uniteLookupData]);

  return (
    <FormListBase data={channelManagerFormDataSource}>
      <FormListBaseGrid dataSource={channelManagerFormDataSource.dataSource}
        onFocusedRowChanged={(e: any) => {
          if (mode === Mode.Modifier) {
            setMode(Mode.Lecture);
          }
          if (setSelectedValue && e.row?.data) {
            setSelectedValue(e.row.data);
          }
        }}>
        <Column key={"grid-col-unite"} dataField={"idUnite"} caption="Unité" allowHiding={false}>
          <Lookup {...uniteEditorOptions} />
        </Column>
        <Column key={"grid-col-prix"} dataField={"prix"} caption="Prix" dataType="number" format="#,##0.00 $" allowHiding={false} cssClass="channel-manager-prix-col" />
        <Column key={"grid-col-id-wubook"} dataField={"idWubook"} caption="ID Wubook" allowHiding={false} cssClass="channel-manager-prix-col"/>
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedUniteCode || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      {!isModeSelection || isModeSelection && mode !== Mode.Lecture
        ? <FormListBaseTab>
          <TabItem title="Informations">
            <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedChannelManager || {} as IChannelManager} className={"forms gestion-channel-manager-form"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={handleFieldDataChanged}>
              <GroupItem>
                <SimpleItem key={"frm-idUnite"} dataField={"idUnite"} editorType={"dxSelectBox"} editorOptions={uniteEditorOptions}>
                  <Label text={"Unité"} />
                </SimpleItem>
                <SimpleItem key={"frm-prix"} dataField={"prix"} editorType={"dxNumberBox"} editorOptions={prixEditorOptions}>
                  <Label text={"Prix"} />
                </SimpleItem>
                <SimpleItem
                  cssClass={"info-wubook-item"}
                  render={() => (
                    <div className="info-wubook">
                      <div className="info-wubook__title">Info WuBook</div>
                      <div className="info-wubook__body">
                        <div className="info-wubook__row">
                          <span className="info-wubook__label">Id :</span>
                          <span className="info-wubook__value">{selectedChannelManager?.idWubook ?? 0}</span>
                        </div>
                        <div className="info-wubook__row">
                          <span className="info-wubook__label">Type :</span>
                          <span className="info-wubook__value">{selectedChannelManager?.idTypeWubook ?? 0}</span>
                        </div>
                        <div className="info-wubook__row">
                          <span className="info-wubook__label">Code d'unité :</span>
                          <span className="info-wubook__value">{selectedUniteCode || ''}</span>
                        </div>
                      </div>
                    </div>
                  )}
                >
                  <Label visible={false} />
                </SimpleItem>
              </GroupItem>
            </Form>
          </TabItem>
          <TabItem title="Système"><FormSysteme formData={systemeFormData} /></TabItem>
        </FormListBaseTab> : null}
    </FormListBase>
  );
}
